import React, { useState } from 'react';
import { FiMaximize } from 'react-icons/fi';
import ZoneScanOverlay from './ZoneScanOverlay';
import type { ScannedZone } from './ZoneScanOverlay';
import './ZoneScanner.scss';

interface ZoneScanButtonProps {
  onSelect: (zone: ScannedZone) => void;
  companyId: string;
  locationId?: string;
  title?: string;
  hint?: string;
  zoneTypes?: string[];
  excludeZoneIds?: string[];
  excludeMessage?: string;
  allowedZones?: ScannedZone[] | null;
  allowedZonesMessage?: string;
  manualEntryLabel?: string;
  disabled?: boolean;
  /** Show the word "Scan" next to the icon. Off in tight rows next to a select. */
  showLabel?: boolean;
  label?: string;
  className?: string;
}

/**
 * Opens the zone-scan camera overlay. Sits next to (never instead of) the manual zone picker
 * on every screen that needs a zone — scanning is the fast path, typing is the fallback when a
 * label is torn or the camera is unavailable.
 *
 * The overlay only exists while open: mounting is what claims the shared camera, and unmounting
 * is what hands it back to the page underneath.
 */
const ZoneScanButton = ({
  onSelect,
  disabled = false,
  showLabel = false,
  label = 'Scan',
  className,
  ...overlayProps
}: ZoneScanButtonProps) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        className={['zone-scan-trigger', className].filter(Boolean).join(' ')}
        onClick={() => setOpen(true)}
        disabled={disabled}
        aria-label={overlayProps.title || 'Scan zone'}
      >
        <FiMaximize aria-hidden />
        {showLabel && <span className="zone-scan-trigger__label">{label}</span>}
      </button>
      {open && (
        <ZoneScanOverlay
          {...overlayProps}
          onClose={() => setOpen(false)}
          onSelect={(zone) => {
            setOpen(false);
            onSelect(zone);
          }}
        />
      )}
    </>
  );
};

export default ZoneScanButton;
